type MessageHandler = (data: unknown) => void;

export class WsClient {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<MessageHandler>>();
  private sessionId: string;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30_000;
  // Live-batch Task 1 — refcounted desired channels. The server drops any
  // frame sent before its `auth_ok` (first frame must be `auth`), and a new
  // socket after a reconnect starts with only the auto-subscribed channels.
  // Tracking desire client-side lets subscribe() queue until authed and lets
  // every reconnect's auth_ok replay the full set. Refcounts (not a Set)
  // because independent hooks may want the same channel.
  private desired = new Map<string, number>();
  private authed = false;

  constructor(sessionId: string) {
    this.sessionId = sessionId;
  }

  connect(): void {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${window.location.host}/ws`;

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.reconnectDelay = 1000;
      this.authed = false; // every new socket must re-auth before subscribes
      // Authenticate
      this.ws?.send(JSON.stringify({ type: "auth", sessionId: this.sessionId }));
    };

    this.ws.onmessage = (event) => {
      let msg: { type: string; [key: string]: unknown };
      try {
        msg = JSON.parse(event.data as string);
      } catch {
        return;
      }

      if (msg.type === "ping") {
        this.ws?.send(JSON.stringify({ type: "pong" }));
        return;
      }

      if (msg.type === "auth_ok") {
        this.authed = true;
        // Flush every desired channel — initial connect and reconnects alike.
        for (const channel of this.desired.keys()) {
          this.sendFrame("subscribe", channel);
        }
        // Fall through: external auth_ok handlers still fire below.
      }

      const handlers = this.handlers.get(msg.type);
      if (handlers) {
        for (const handler of handlers) {
          handler(msg);
        }
      }

      // Also fire wildcard handlers
      const wildcardHandlers = this.handlers.get("*");
      if (wildcardHandlers) {
        for (const handler of wildcardHandlers) {
          handler(msg);
        }
      }
    };

    this.ws.onclose = () => {
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  on(type: string, handler: MessageHandler): () => void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler);

    return () => {
      this.handlers.get(type)?.delete(handler);
    };
  }

  /**
   * B1 (ff-web) — thin wrapper over `on` for the common case: the wire
   * format is `{ type, payload }`, but `on` hands handlers the whole
   * envelope. Consumers were hand-rolling `.payload` unwrapping (some
   * correctly, one — useDocumentMovedBump — not at all, silently breaking
   * it in production). `onPayload` centralizes the unwrap.
   *
   * When `payload` is null/undefined, the handler is SKIPPED rather than
   * invoked with `undefined` — a message with no payload has nothing for
   * a payload-shaped handler to act on, and silently calling it with
   * `undefined` would just push the null-check into every caller again.
   */
  onPayload(type: string, handler: (payload: unknown) => void): () => void {
    return this.on(type, (msg) => {
      const payload = (msg as { payload?: unknown }).payload;
      if (payload === null || payload === undefined) return;
      handler(payload);
    });
  }

  subscribe(channel: string): void {
    const count = (this.desired.get(channel) ?? 0) + 1;
    this.desired.set(channel, count);
    // Only the 0→1 transition puts a frame on the wire; before auth_ok the
    // flush in the `auth_ok` handler covers it.
    if (count === 1 && this.authed) {
      this.sendFrame("subscribe", channel);
    }
  }

  unsubscribe(channel: string): void {
    const count = this.desired.get(channel);
    if (count === undefined) return;
    if (count > 1) {
      this.desired.set(channel, count - 1);
      return;
    }
    this.desired.delete(channel);
    this.sendFrame("unsubscribe", channel);
  }

  private sendFrame(type: "subscribe" | "unsubscribe", channel: string): void {
    // Guard: the socket can close between auth_ok arriving and this send.
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, channel }));
    }
  }

  disconnect(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
      this.connect();
    }, this.reconnectDelay);
  }
}
